import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import axios from "axios";
import config from "../config";
import {
  exchangeDesktopLoginCode,
  exchangeLoginAuthCode,
  exchangeMcpHandoff,
  getMcpHandoffTicket,
  isLoginOAuthCallback,
  stripMcpHandoffTicket,
} from "../services/auth";
import {
  installSessionExpiryFetchGuard,
  isAlreadyOnLoginPage,
  redirectToLoginOnSessionExpired,
  registerSessionExpiredHandler,
  resetSessionExpiredRedirectGate,
} from "../services/sessionExpiry";
import { getModeFromPathname, resolveSelectedMode } from "../utils/appModeFromPath";
import { notifyFreshLogin } from "../services/authLifecycle";
import { isDesktopLoginState } from "../utils/desktopCallback";
import { getLockedAppMode } from "../utils/lockedAppMode";

const AuthContext = createContext();
const MODE_STORAGE_KEY = "selectedMode";

export const getDefaultAuthenticatedPath = (selectedMode) => {
  const mode = getLockedAppMode() ?? selectedMode;
  if (mode === "developer") {
    return "/connectors";
  }
  return "/business/action-items";
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Stores user information
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Tracks login status
  const [loading, setLoading] = useState(true); // Tracks session validation/loading
  const [selectedMode, setSelectedMode] = useState("business");
  const [desktopAuthError, setDesktopAuthError] = useState("");

  const applySelectedMode = useCallback((mode) => {
    setSelectedMode(mode);
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, []);

  const syncModeFromUrl = useCallback((pathname, search = "") => {
    const urlMode = getModeFromPathname(pathname, search);
    if (urlMode) {
      applySelectedMode(urlMode);
    }
  }, [applySelectedMode]);

  const applyUserSessionState = useCallback((nextUser) => {
    const storedMode = localStorage.getItem(MODE_STORAGE_KEY);
    const allowedMode = resolveSelectedMode(
      storedMode,
      window.location.pathname,
      window.location.search
    );
    setUser(nextUser);
    applySelectedMode(allowedMode);
  }, [applySelectedMode]);

  const validateSession = useCallback(async () => {
    if (getMcpHandoffTicket()) {
      // Arrived from the Claude login page: already verified by Google there.
      let sessionData = null;
      try {
        sessionData = await exchangeMcpHandoff();
      } catch (error) {
        console.error("MCP handoff failed:", error);
      }
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + stripMcpHandoffTicket()
      );
      if (sessionData?.user_info) {
        notifyFreshLogin();
        localStorage.setItem("user_info", JSON.stringify(sessionData.user_info));
        applyUserSessionState(sessionData.user_info);
        resetSessionExpiredRedirectGate();
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }
      // Expired or already used: fall through to the normal session check.
    }

    if (isLoginOAuthCallback()) {
      try {
        const sessionData = await exchangeLoginAuthCode();
        if (sessionData?.user_info) {
          // New Google sign-in/sign-up — reset paste-escape skip so empty
          // accounts land on /onboarding until they connect a source.
          notifyFreshLogin();
          localStorage.setItem("user_info", JSON.stringify(sessionData.user_info));
          applyUserSessionState(sessionData.user_info);
          resetSessionExpiredRedirectGate();
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Login OAuth callback failed:", error);
        setUser(null);
        setIsAuthenticated(false);
        // No benefit showing the raw Google/token error — hard-refresh to clean login.
        redirectToLoginOnSessionExpired();
        return;
      } finally {
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
      }
      return;
    }

    try {
      const response = await axios.get(
        `${config.BACKEND_URL}/validate-session`,
        { withCredentials: true }
      );
      const validatedUser = response?.data?.user_info;
      if (response.data.message === "Session is valid" && validatedUser) {
        localStorage.setItem("user_info", JSON.stringify(validatedUser));
        applyUserSessionState(validatedUser);
        resetSessionExpiredRedirectGate();
        setIsAuthenticated(true);
      } else if (!isAlreadyOnLoginPage()) {
        // Valid response but no session — refresh onto login rather than a blank auth shell.
        redirectToLoginOnSessionExpired();
        return;
      }
    } catch (error) {
      console.error("Session validation failed:", error);
      setUser(null);
      setIsAuthenticated(false);
      // Any failure to validate the session (invalid/expired cookie, network
      // blip, or a backend 5xx) means we can't confirm the user is signed
      // in — redirect to login rather than leaving a half-authenticated
      // shell on screen. Stay put on `/` itself to avoid a reload loop when
      // no cookie exists.
      if (!isAlreadyOnLoginPage()) {
        redirectToLoginOnSessionExpired();
        return;
      }
      const storedMode = localStorage.getItem(MODE_STORAGE_KEY);
      applySelectedMode(
        resolveSelectedMode(
          storedMode,
          window.location.pathname,
          window.location.search
        )
      );
    } finally {
      setLoading(false);
    }
  }, [applyUserSessionState, applySelectedMode]);

  const completeLogin = (nextUser) => {
    localStorage.setItem("user_info", JSON.stringify(nextUser));
    applyUserSessionState(nextUser);
    resetSessionExpiredRedirectGate();
    setIsAuthenticated(true);
  };

  const toggleMode = () => {
    if (!user) {
      return "business";
    }
    const nextMode = selectedMode === "business" ? "developer" : "business";
    applySelectedMode(nextMode);
    return nextMode;
  };

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  // Desktop build only: Google's code comes back to a loopback server in
  // electron/main.js (not this window's URL), which forwards it here over IPC.
  useEffect(() => {
    if (!window.starmeshDesktop?.isElectron) {
      return undefined;
    }
    return window.starmeshDesktop.onOAuthCallback(async (payload) => {
      if (!isDesktopLoginState(payload?.state)) {
        return;
      }
      setDesktopAuthError("");
      try {
        notifyFreshLogin();
        // Main process exchanges the Google code (avoids app:// CORS) and
        // stores the session cookie in Chromium's jar before notifying us.
        if (payload?.user_info) {
          completeLogin(payload.user_info);
          return;
        }
        if (payload?.error) {
          throw new Error(payload.error);
        }
        // Older builds still forward { code }; exchange in the renderer.
        if (payload?.code) {
          const sessionData = await exchangeDesktopLoginCode(payload.code);
          if (sessionData?.user_info) {
            completeLogin(sessionData.user_info);
          }
        }
      } catch (error) {
        console.error("Desktop login OAuth callback failed:", error);
        setDesktopAuthError(
          error?.message || "Desktop sign-in failed. Please try again."
        );
      } finally {
        setLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    installSessionExpiryFetchGuard();
    registerSessionExpiredHandler(() => {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    });
    return () => {
      registerSessionExpiredHandler(null);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        completeLogin,
        selectedMode,
        setSelectedMode: applySelectedMode,
        syncModeFromUrl,
        toggleMode,
        isAuthenticated,
        setIsAuthenticated,
        loading,
        validateSession,
        desktopAuthError,
        setDesktopAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
